/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react'

type BackHandler = (() => void) | null

interface NavBackContextType {
  backHandler: BackHandler
  setBackHandler: (handler: BackHandler) => void
}

const NavBackContext = createContext<NavBackContextType>({
  backHandler: null,
  setBackHandler: () => {}
})

/**
 * Lets a page override the shared app-header back arrow with a custom action
 * (e.g. a step-aware wizard back). Pages that register no handler keep the
 * default <Link>-to-parent behavior in MobileNavigation.
 */
export const NavBackProvider = ({ children }: { children: ReactNode }) => {
  const [backHandler, setBackHandlerState] = useState<BackHandler>(null)

  // Wrap so callers pass the handler itself; the extra thunk avoids React's
  // "updater function" interpretation of a function passed to a state setter.
  const setBackHandler = useCallback((handler: BackHandler) => {
    setBackHandlerState(() => handler)
  }, [])

  const value = useMemo(() => ({ backHandler, setBackHandler }), [backHandler, setBackHandler])

  return <NavBackContext.Provider value={value}>{children}</NavBackContext.Provider>
}

export const useNavBack = (): NavBackContextType => useContext(NavBackContext)
